/**
 * WhatsApp Campaigns API Service
 *
 * HTTP client for WhatsApp campaign endpoints.
 * Uses mock data for templates and segments in this MVP — the real
 * integration will call Meta WhatsApp Business API + Deenex segments service.
 */

import { apiClient as api } from "../../services/api/client";
import { segmentsApi } from "../segments/api";
import type {
  WhatsAppCampaign,
  WhatsAppTemplate,
  SegmentSummary,
  TemplateCategory,
  WhatsAppTemplateHeader,
  WhatsAppButton,
} from "./types";

type ApiResponse<T> = { success: boolean; data: T; error?: string };

/**
 * Las plantillas de demo de abajo sólo tienen sentido en el modo mock (GH Pages, sin backend).
 * Contra un backend real, mezclarlas o devolverlas ante un error mostraba plantillas «aprobadas»
 * que no existen. Se lee en cada llamada (no al importar) para que el modo se pueda probar.
 */
const esModoMock = () => import.meta.env.VITE_MOCK_MODE === "true";

/** Mensaje de un error de axios, para devolverlo como success:false. */
const mensajeDeError = (err: any, porDefecto: string): string =>
  err?.response?.data?.error || err?.message || porDefecto;

/* ─── Mock WhatsApp templates (in production these come from Meta) ─── */
const MOCK_TEMPLATES: WhatsAppTemplate[] = [
  {
    id: "tpl-promo-discount",
    name: "promo_descuento",
    category: "marketing",
    language: "es_AR",
    approvalStatus: "approved",
    header: { type: "image", variableCount: 0 },
    body:
      "Hola {{1}}, tenemos una oferta especial para vos 🎉\n\n*{{2}}% OFF* en toda la tienda usando el código *{{3}}*\n\nVálido hasta fin de mes.",
    variableCount: 3,
    footer: "Si no querés recibir más promos, escribí BAJA.",
    buttons: [
      { type: "url", label: "Comprar ahora", url: "https://tienda.deenex.com/?utm={{1}}" },
      { type: "quick_reply", label: "Más info" },
    ],
    estimatedCostUSD: 0.0573, // Argentina marketing rate
  },
  {
    id: "tpl-cart-abandoned",
    name: "carrito_abandonado",
    category: "marketing",
    language: "es_AR",
    approvalStatus: "approved",
    body:
      "Hola {{1}} 👋\n\nDejaste *{{2}} producto/s* en tu carrito. ¿Querés terminar tu compra?\n\nTotal: ${{3}}",
    variableCount: 3,
    buttons: [
      { type: "url", label: "Volver al carrito", url: "https://tienda.deenex.com/cart" },
      { type: "quick_reply", label: "Hablar con asesor" },
    ],
    estimatedCostUSD: 0.0573,
  },
  {
    id: "tpl-order-confirmed",
    name: "pedido_confirmado",
    category: "utility",
    language: "es_AR",
    approvalStatus: "approved",
    body:
      "✅ Pedido confirmado\n\nHola {{1}}, tu pedido *#{{2}}* fue recibido.\nTotal: *${{3}}*\nLlega en aprox. {{4}} min.",
    variableCount: 4,
    buttons: [
      { type: "url", label: "Ver pedido", url: "https://tienda.deenex.com/orders/{{1}}" },
    ],
    estimatedCostUSD: 0.0098, // Utility is cheaper
  },
  {
    id: "tpl-birthday",
    name: "feliz_cumple",
    category: "marketing",
    language: "es_AR",
    approvalStatus: "approved",
    header: { type: "text", text: "🎂 ¡Feliz cumple, {{1}}!", variableCount: 1 },
    body:
      "Para festejarlo, te regalamos *{{1}}% OFF* en tu próxima compra.\n\nUsá el código *{{2}}* hasta el {{3}}.",
    variableCount: 3,
    buttons: [{ type: "url", label: "Usar regalo", url: "https://tienda.deenex.com/" }],
    estimatedCostUSD: 0.0573,
  },
  {
    id: "tpl-reactivation",
    name: "reactivacion_inactivos",
    category: "marketing",
    language: "es_AR",
    approvalStatus: "pending",
    body:
      "Hola {{1}}, te extrañamos 😢\n\nVolvé y disfrutá *{{2}}% OFF* en tu próximo pedido.\n\nVálido por 7 días.",
    variableCount: 2,
    buttons: [{ type: "quick_reply", label: "¡Me sumo!" }],
    estimatedCostUSD: 0.0573,
  },
  {
    id: "tpl-otp",
    name: "codigo_verificacion",
    category: "authentication",
    language: "es_AR",
    approvalStatus: "approved",
    body: "*{{1}}* es tu código de verificación. No lo compartas con nadie.",
    variableCount: 1,
    footer: "Este código vence en 5 minutos.",
    estimatedCostUSD: 0.0379,
  },
];

/* ─── API surface ─── */
export const whatsappApi = {
  /** Get saved segments — same real store as SegmentsPage */
  async getSegments(brandId: string): Promise<ApiResponse<SegmentSummary[]>> {
    const res = await segmentsApi.list(brandId, "saved");
    if (!res.success) return { success: false, data: [], error: res.error };
    const summaries: SegmentSummary[] = res.data.map((s) => ({
      id: s.id,
      name: s.name || "Segmento sin nombre",
      lastKnownCount: s.lastKnownCount || 0,
      status: s.status,
    }));
    return { success: true, data: summaries };
  },

  /**
   * Get available WhatsApp templates for this brand.
   * Tries the real backend first; falls back to the in-memory mocks if the
   * API is unreachable so the UI keeps working offline.
   */
  async getTemplates(brandId: string): Promise<ApiResponse<WhatsAppTemplate[]>> {
    try {
      const response = await api.get(`/whatsapp/templates`, {
        params: { brandId },
      });
      // Merge: show backend templates first, then any built-in mocks that
      // are not yet persisted (so the demo seed templates remain visible)
      const fromApi = (response.data?.data || []) as WhatsAppTemplate[];
      if (!esModoMock()) return { success: true, data: fromApi };
      const apiNames = new Set(fromApi.map((t) => t.name));
      const merged = [
        ...fromApi,
        ...MOCK_TEMPLATES.filter((m) => !apiNames.has(m.name)),
      ];
      return { success: true, data: merged };
    } catch (err: any) {
      if (esModoMock()) return { success: true, data: MOCK_TEMPLATES };
      return { success: false, data: [], error: mensajeDeError(err, "Error al cargar las plantillas") };
    }
  },

  /** Get a single template by id */
  async getTemplateById(id: string): Promise<ApiResponse<WhatsAppTemplate>> {
    try {
      const response = await api.get(`/whatsapp/templates/${id}`);
      return response.data;
    } catch {
      // Fallback a las plantillas de demo, sólo en modo mock (ver esModoMock).
      const mock = esModoMock() ? MOCK_TEMPLATES.find((t) => t.id === id) : undefined;
      if (mock) return { success: true, data: mock };
      return { success: false, data: {} as WhatsAppTemplate, error: "Template not found" };
    }
  },

  /**
   * Create a "mock" pre-approved template (for demos / offline dev).
   * No pasa por Meta. Devuelve una plantilla `approved` lista para usar.
   */
  async createMockTemplate(params: {
    brandId: string;
    name: string;
    description: string;
  }): Promise<ApiResponse<WhatsAppTemplate>> {
    try {
      const response = await api.post(`/whatsapp/templates/mock`, params);
      return response.data;
    } catch (err: any) {
      return {
        success: false,
        data: {} as WhatsAppTemplate,
        error:
          err?.response?.data?.error ||
          err.message ||
          "Error al crear la plantilla",
      };
    }
  },

  /** Create a new custom template */
  async createTemplate(params: {
    brandId: string;
    name: string;
    category: TemplateCategory;
    language?: string;
    header?: WhatsAppTemplateHeader;
    body: string;
    footer?: string;
    buttons?: WhatsAppButton[];
  }): Promise<ApiResponse<WhatsAppTemplate>> {
    try {
      const response = await api.post(`/whatsapp/templates`, params);
      return response.data;
    } catch (err: any) {
      return {
        success: false,
        data: {} as WhatsAppTemplate,
        error:
          err?.response?.data?.error ||
          err.message ||
          "Error al crear la plantilla",
      };
    }
  },

  /** Update an existing template */
  async updateTemplate(
    id: string,
    params: Partial<{
      name: string;
      category: TemplateCategory;
      language: string;
      header: WhatsAppTemplateHeader;
      body: string;
      footer: string;
      buttons: WhatsAppButton[];
    }>,
  ): Promise<ApiResponse<WhatsAppTemplate>> {
    try {
      const response = await api.patch(`/whatsapp/templates/${id}`, params);
      return response.data;
    } catch (err: any) {
      return {
        success: false,
        data: {} as WhatsAppTemplate,
        error:
          err?.response?.data?.error ||
          err.message ||
          "Error al actualizar la plantilla",
      };
    }
  },

  /** Delete a template */
  async deleteTemplate(id: string): Promise<ApiResponse<null>> {
    try {
      const response = await api.delete(`/whatsapp/templates/${id}`);
      return response.data;
    } catch (err: any) {
      return {
        success: false,
        data: null,
        error:
          err?.response?.data?.error ||
          err.message ||
          "Error al borrar la plantilla",
      };
    }
  },

  /** Duplicate a template */
  async duplicateTemplate(id: string): Promise<ApiResponse<WhatsAppTemplate>> {
    try {
      const response = await api.post(`/whatsapp/templates/${id}/duplicate`);
      return response.data;
    } catch (err: any) {
      return {
        success: false,
        data: {} as WhatsAppTemplate,
        error:
          err?.response?.data?.error ||
          err.message ||
          "Error al duplicar la plantilla",
      };
    }
  },

  /** Submit a pending template to Meta for approval */
  async submitTemplateToMeta(id: string): Promise<ApiResponse<WhatsAppTemplate>> {
    try {
      const response = await api.post(`/whatsapp/templates/${id}/submit`);
      return response.data;
    } catch (err: any) {
      return {
        success: false,
        data: {} as WhatsAppTemplate,
        error:
          err?.response?.data?.error ||
          err.message ||
          "Error al enviar la plantilla a Meta",
      };
    }
  },

  /**
   * Create a WhatsApp campaign.
   *
   * Un error es un error: antes el catch devolvía success:true con una campaña inventada, y con
   * el backend legacy apagado (404) la pantalla seguía como si se hubiera creado.
   */
  async create(params: {
    brandId: string;
    segmentId: string;
    templateId: string;
    bodyVariables: Record<string, string>;
    headerVariable?: string;
    headerMediaUrl?: string;
  }): Promise<ApiResponse<WhatsAppCampaign>> {
    try {
      const response = await api.post("/whatsapp/campaigns", params);
      return response.data;
    } catch (err: any) {
      return {
        success: false,
        data: {} as WhatsAppCampaign,
        error: mensajeDeError(err, "Error al crear la campaña"),
      };
    }
  },

  /**
   * Send a campaign immediately. Un error (incluido el 501 del backend, que no manda nada por
   * Meta) vuelve como success:false: antes el catch devolvía status "sent" y la pantalla decía
   * «enviada» sin que saliera un mensaje.
   */
  async send(id: string): Promise<ApiResponse<WhatsAppCampaign>> {
    try {
      const response = await api.post(`/whatsapp/campaigns/${id}/send`);
      return response.data;
    } catch (err: any) {
      return {
        success: false,
        data: {} as WhatsAppCampaign,
        error: mensajeDeError(err, "Error al enviar la campaña"),
      };
    }
  },

  /** Schedule a campaign for later. Un error vuelve como success:false (ver send). */
  async schedule(
    id: string,
    scheduledAt: string,
  ): Promise<ApiResponse<WhatsAppCampaign>> {
    try {
      const response = await api.post(`/whatsapp/campaigns/${id}/schedule`, {
        scheduledAt,
      });
      return response.data;
    } catch (err: any) {
      return {
        success: false,
        data: {} as WhatsAppCampaign,
        error: mensajeDeError(err, "Error al programar la campaña"),
      };
    }
  },

  /** Aggregate analytics across campaigns */
  async analytics(
    brandId: string,
    days: number = 30,
  ): Promise<
    ApiResponse<{
      totals: {
        sent: number;
        delivered: number;
        read: number;
        replied: number;
        failed: number;
        costUSD: number;
        campaignsCount: number;
      };
      rates: { delivery: number; read: number; reply: number };
      perDay: Array<{
        day: string;
        entregado: number;
        leido: number;
        respondido: number;
      }>;
      recentCampaigns: WhatsAppCampaign[];
    }>
  > {
    try {
      const response = await api.get(`/whatsapp/analytics`, {
        params: { brandId, days },
      });
      return response.data;
    } catch (err: any) {
      return {
        success: false,
        data: {} as any,
        error:
          err?.response?.data?.error ||
          err.message ||
          "Error al cargar las métricas",
      };
    }
  },
};

export default whatsappApi;

/* ──────────────────────────────────────────────────────────────────────
 * WhatsApp Accounts API (vinculadas vía QR)
 *
 * Endpoints:
 *   GET    /whatsapp/accounts                     → list
 *   POST   /whatsapp/accounts/link                → init pairing (returns QR)
 *   POST   /whatsapp/accounts/confirm             → simulate scan / complete
 *   GET    /whatsapp/accounts/:id                 → detail
 *   DELETE /whatsapp/accounts/:id                 → unlink
 *   POST   /whatsapp/accounts/:id/disconnect      → force disconnect
 *   POST   /whatsapp/accounts/:id/reconnect       → trigger reconnect
 *   POST   /whatsapp/accounts/:id/set-default     → mark as default
 * ────────────────────────────────────────────────────────────────────── */

import type {
  WhatsAppAccount,
  LinkAccountRequest,
  LinkAccountQrResponse,
  WhatsAppBusinessProfile,
  WhatsAppAutoReply,
} from "./types";

export const whatsappAccountsApi = {
  async list(): Promise<ApiResponse<WhatsAppAccount[]>> {
    try {
      const r = await api.get("/whatsapp/accounts");
      return r.data;
    } catch (err: any) {
      return {
        success: false,
        data: [],
        error: err?.response?.data?.error || err.message || "Error al cargar cuentas",
      };
    }
  },

  async startLink(payload: LinkAccountRequest): Promise<ApiResponse<LinkAccountQrResponse & { accountId: string }>> {
    try {
      const r = await api.post("/whatsapp/accounts/link", payload);
      return r.data;
    } catch (err: any) {
      return {
        success: false,
        data: null as any,
        error: err?.response?.data?.error || err.message || "Error al iniciar vinculación",
      };
    }
  },

  /** Simula que el celular escaneó el QR — completa el pairing. */
  async confirmLink(pairingId: string): Promise<ApiResponse<WhatsAppAccount>> {
    try {
      const r = await api.post("/whatsapp/accounts/confirm", { pairingId });
      return r.data;
    } catch (err: any) {
      return {
        success: false,
        data: null as any,
        error: err?.response?.data?.error || err.message || "Error al confirmar",
      };
    }
  },

  async remove(id: string): Promise<ApiResponse<void>> {
    try {
      const r = await api.delete(`/whatsapp/accounts/${id}`);
      return r.data;
    } catch (err: any) {
      return {
        success: false,
        data: undefined as any,
        error: err?.response?.data?.error || err.message || "Error al desvincular",
      };
    }
  },

  async disconnect(id: string): Promise<ApiResponse<WhatsAppAccount>> {
    try {
      const r = await api.post(`/whatsapp/accounts/${id}/disconnect`);
      return r.data;
    } catch (err: any) {
      return {
        success: false,
        data: null as any,
        error: err?.response?.data?.error || err.message || "Error al desconectar",
      };
    }
  },

  async reconnect(id: string): Promise<ApiResponse<WhatsAppAccount>> {
    try {
      const r = await api.post(`/whatsapp/accounts/${id}/reconnect`);
      return r.data;
    } catch (err: any) {
      return {
        success: false,
        data: null as any,
        error: err?.response?.data?.error || err.message || "Error al reconectar",
      };
    }
  },

  async setDefault(id: string): Promise<ApiResponse<WhatsAppAccount>> {
    try {
      const r = await api.post(`/whatsapp/accounts/${id}/set-default`);
      return r.data;
    } catch (err: any) {
      return {
        success: false,
        data: null as any,
        error: err?.response?.data?.error || err.message || "Error al fijar default",
      };
    }
  },

  /* ─── Setup wizard ─── */
  async getById(id: string): Promise<ApiResponse<WhatsAppAccount>> {
    try {
      const r = await api.get(`/whatsapp/accounts/${id}`);
      return r.data;
    } catch (err: any) {
      return {
        success: false,
        data: null as any,
        error: err?.response?.data?.error || err.message || "Error al cargar cuenta",
      };
    }
  },

  async saveBusinessProfile(
    id: string,
    profile: WhatsAppBusinessProfile,
  ): Promise<ApiResponse<WhatsAppAccount>> {
    try {
      const r = await api.patch(
        `/whatsapp/accounts/${id}/setup/business-profile`,
        profile,
      );
      return r.data;
    } catch (err: any) {
      return {
        success: false,
        data: null as any,
        error: err?.response?.data?.error || err.message || "Error al guardar perfil",
      };
    }
  },

  async sendTestMessage(
    id: string,
    payload: { toNumber: string; message: string },
  ): Promise<ApiResponse<{ messageId: string; sentAt: string; sentTo: string; preview: string }>> {
    try {
      const r = await api.post(
        `/whatsapp/accounts/${id}/setup/test-message`,
        payload,
      );
      return r.data;
    } catch (err: any) {
      return {
        success: false,
        data: null as any,
        error: err?.response?.data?.error || err.message || "Error enviando test",
      };
    }
  },

  async confirmTestReceived(id: string): Promise<ApiResponse<WhatsAppAccount>> {
    try {
      const r = await api.patch(
        `/whatsapp/accounts/${id}/setup/test-message/confirm`,
      );
      return r.data;
    } catch (err: any) {
      return {
        success: false,
        data: null as any,
        error: err?.response?.data?.error || err.message || "Error al confirmar",
      };
    }
  },

  async saveAutoReply(
    id: string,
    autoReply: WhatsAppAutoReply,
  ): Promise<ApiResponse<WhatsAppAccount>> {
    try {
      const r = await api.patch(
        `/whatsapp/accounts/${id}/setup/auto-reply`,
        autoReply,
      );
      return r.data;
    } catch (err: any) {
      return {
        success: false,
        data: null as any,
        error: err?.response?.data?.error || err.message || "Error al guardar auto-respuestas",
      };
    }
  },

  async assignObjectives(
    id: string,
    objectiveIds: string[],
  ): Promise<ApiResponse<WhatsAppAccount>> {
    try {
      const r = await api.patch(
        `/whatsapp/accounts/${id}/setup/objectives`,
        { objectiveIds },
      );
      return r.data;
    } catch (err: any) {
      return {
        success: false,
        data: null as any,
        error: err?.response?.data?.error || err.message || "Error al asignar objetivos",
      };
    }
  },

  async acknowledgeWarmup(id: string): Promise<ApiResponse<WhatsAppAccount>> {
    try {
      const r = await api.patch(
        `/whatsapp/accounts/${id}/setup/warmup-ack`,
      );
      return r.data;
    } catch (err: any) {
      return {
        success: false,
        data: null as any,
        error: err?.response?.data?.error || err.message || "Error en ack",
      };
    }
  },

  async activate(id: string): Promise<ApiResponse<WhatsAppAccount>> {
    try {
      const r = await api.post(`/whatsapp/accounts/${id}/activate`);
      return r.data;
    } catch (err: any) {
      return {
        success: false,
        data: null as any,
        error: err?.response?.data?.error || err.message || "Error al activar",
      };
    }
  },

  async pause(id: string): Promise<ApiResponse<WhatsAppAccount>> {
    try {
      const r = await api.post(`/whatsapp/accounts/${id}/pause`);
      return r.data;
    } catch (err: any) {
      return {
        success: false,
        data: null as any,
        error: err?.response?.data?.error || err.message || "Error al pausar",
      };
    }
  },
};
